/**
 * Deploy tokens (`kin_dpt_*`) — manager surface for CI deploys.
 *
 * Distinct from `UserTokenSection`: deploy tokens are outbound bearers
 * for CI calling platform MCP (kin_deploy/promote/...), not inbound
 * bearers reaching the app. They're always per-app, always manager-
 * minted, scoped to a closed allowlist of MCP tools frozen at mint time.
 *
 * See docs/adr/0007-deploy-tokens.md for the full model, and
 * docs/deploying-from-ci.md for the GitHub Actions integration this
 * surface is here to support.
 */

import { useState } from 'react';
import { api } from '../api/client.js';
import type {
  DeployTokenListResponse,
  DeployTokenWithCreator,
  MintDeployTokenResponse,
} from '../api/types.js';
import { IconBolt, IconCopy, IconShield, IconTrash, IconX, IconExternal } from './Icons.js';
import { Button, Card, CardHeader, EmptyState, Field, Mono, inputClass } from './ui.js';
import { relTime } from '../lib/format.js';
import { runAction, type ActionMessage } from '../lib/runAction.js';

// Sane defaults so a manager wiring CI doesn't have to think about which
// tools belong in the allowlist. Mirrors the eight-tool set documented
// in docs/deploying-from-ci.md as the canonical CI scope. The closed
// universe (`allowed_tools_options` from the GET response) is wider —
// users can toggle on the rarer ones when needed.
const DEFAULT_TOOLS = [
  'kin_get_upload_url',
  'kin_check_known_assets',
  'kin_deploy',
  'kin_promote',
  'kin_get_app',
  'kin_get_deployment',
  'kin_list_deployments',
  'kin_get_health',
];

const TTL_OPTIONS: { days: number; label: string }[] = [
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
  { days: 180, label: '180 days' },
  { days: 365, label: '365 days (recommended)' },
];

export interface DeployTokenSectionProps {
  slug: string;
  payload: DeployTokenListResponse;
  busy: boolean;
  setBusy: (busy: boolean) => void;
  setActionMessage: (msg: ActionMessage | null) => void;
  reload: () => Promise<void>;
}

export function DeployTokenSection({
  slug,
  payload,
  busy,
  setBusy,
  setActionMessage,
  reload,
}: DeployTokenSectionProps) {
  const [label, setLabel] = useState('');
  const [selectedTools, setSelectedTools] = useState<Set<string>>(new Set(DEFAULT_TOOLS));
  const [ttlDays, setTtlDays] = useState(365);
  const [revealed, setRevealed] = useState<MintDeployTokenResponse | null>(null);
  const [copyConfirm, setCopyConfirm] = useState(false);

  const allowedToolsOptions = payload.allowed_tools_options;
  const activeTokens = payload.tokens.filter((t) => !t.revoked_at);
  const revokedTokens = payload.tokens.filter((t) => t.revoked_at);

  function toggleTool(tool: string) {
    setSelectedTools((prev) => {
      const next = new Set(prev);
      if (next.has(tool)) next.delete(tool);
      else next.add(tool);
      return next;
    });
  }

  async function mint(e: React.FormEvent) {
    e.preventDefault();
    if (!label.trim() || selectedTools.size === 0) return;
    // Persist user's selected order: send the canonical-set order back
    // (server canonicalizes anyway, but this avoids spurious diffs).
    const tools = allowedToolsOptions.filter((t) => selectedTools.has(t));
    await runAction({
      do: () =>
        api.post<MintDeployTokenResponse>(`/api/apps/${encodeURIComponent(slug)}/deploy-tokens`, {
          label: label.trim(),
          allowed_tools: tools,
          ttl_days: ttlDays,
        }),
      onSuccess: (res) => {
        setRevealed(res);
        setLabel('');
        return `Deploy token created. Copy it now — you won't see it again.`;
      },
      errorPrefix: "Couldn't create deploy token",
      setBusy,
      setMessage: setActionMessage,
      reload,
    });
  }

  async function revoke(t: DeployTokenWithCreator) {
    const ownerLabel = t.created_by.name ?? t.created_by.email ?? t.created_by.user_id;
    if (
      !confirm(
        `Revoke deploy token "${t.label}" (minted by ${ownerLabel})? Any CI run using it will start failing immediately.`,
      )
    )
      return;
    await runAction({
      do: () => api.delete(`/api/apps/${encodeURIComponent(slug)}/deploy-tokens/${t.id}`),
      onSuccess: () => `Revoked "${t.label}".`,
      errorPrefix: "Couldn't revoke",
      setBusy,
      setMessage: setActionMessage,
      reload,
    });
  }

  function copyToken() {
    if (!revealed) return;
    void navigator.clipboard.writeText(revealed.token).then(() => {
      setCopyConfirm(true);
      setTimeout(() => setCopyConfirm(false), 1500);
    });
  }

  return (
    <div className="space-y-5">
      {revealed && (
        <Card>
          <CardHeader
            title="New deploy token — copy it now"
            description="This is the only time you'll see the plaintext. After you dismiss this, only metadata is shown."
            actions={
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setRevealed(null)}
                aria-label="Dismiss"
              >
                <IconX size={13} />
                Done
              </Button>
            }
          />
          <div className="px-5 py-4 space-y-4">
            <div className="flex items-center gap-2">
              <Mono className="flex-1 break-all px-3 py-2 rounded-md bg-surface-2 border border-line text-xs">
                {revealed.token}
              </Mono>
              <Button variant="primary" size="sm" onClick={copyToken}>
                <IconCopy size={13} />
                {copyConfirm ? 'Copied' : 'Copy'}
              </Button>
            </div>
            <div className="space-y-2 text-xs">
              <p className="text-muted font-medium uppercase tracking-wider text-[10px]">Wire CI</p>
              <p className="text-muted">
                Save this as a GitHub Actions secret on the repo or environment that runs your
                deploy workflow. Suggested name:{' '}
                <Mono>KIN_DEPLOY_TOKEN_{slug.replace(/-/g, '_').toUpperCase()}</Mono>. See{' '}
                <a
                  href="https://github.com/MytraAI/kin/blob/main/docs/deploying-from-ci.md"
                  target="_blank"
                  rel="noreferrer"
                  className="hover:text-ink transition-colors inline-flex items-center gap-0.5"
                >
                  docs/deploying-from-ci.md <IconExternal size={11} />
                </a>{' '}
                for the workflow shape.
              </p>
            </div>
          </div>
        </Card>
      )}

      <Card>
        <CardHeader
          title="Mint a deploy token"
          description="One token per CI pipeline. Scope frozen at mint — rotate the token to change scope. Manager-only."
        />
        <form onSubmit={(e) => void mint(e)} className="px-5 py-4 space-y-4">
          <div className="grid sm:grid-cols-[1fr_220px] gap-3">
            <Field label="Label" required>
              <input
                type="text"
                required
                maxLength={64}
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="github-actions"
                className={inputClass}
              />
            </Field>
            <Field label="Lifetime">
              <select
                value={ttlDays}
                onChange={(e) => setTtlDays(Number(e.target.value))}
                className={inputClass}
              >
                {TTL_OPTIONS.map((o) => (
                  <option key={o.days} value={o.days}>
                    {o.label}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <Field label="Allowed MCP tools" required>
            <fieldset className="grid sm:grid-cols-2 gap-x-4 gap-y-2 text-sm border border-line rounded-md px-4 py-3 bg-surface-2">
              <legend className="sr-only">Allowed MCP tools</legend>
              {allowedToolsOptions.map((tool) => (
                <label key={tool} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedTools.has(tool)}
                    onChange={() => toggleTool(tool)}
                    className="accent-brand"
                  />
                  <Mono className="text-xs">{tool}</Mono>
                </label>
              ))}
            </fieldset>
          </Field>

          <div className="flex items-center justify-between flex-wrap gap-2 pt-1">
            <p className="text-xs text-muted inline-flex items-center gap-1.5">
              <IconShield size={12} />
              {selectedTools.size} tool{selectedTools.size === 1 ? '' : 's'} selected. The token
              cannot mint, list, or revoke other tokens.
            </p>
            <Button
              type="submit"
              variant="primary"
              size="md"
              loading={busy}
              disabled={busy || !label.trim() || selectedTools.size === 0}
            >
              Generate
            </Button>
          </div>
        </form>
      </Card>

      <Card>
        <CardHeader
          title="Active deploy tokens"
          description={`Tokens currently authorized to deploy ${slug} from CI.`}
        />
        {activeTokens.length === 0 ? (
          <EmptyState
            icon={<IconBolt size={18} />}
            title="No active deploy tokens"
            description="Mint one above to wire a CI pipeline."
          />
        ) : (
          <ul className="divide-y divide-line">
            {activeTokens.map((t) => (
              <li key={t.id} className="px-5 py-3 text-sm">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1 space-y-1.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Mono className="font-medium">{t.label}</Mono>
                    </div>
                    <p className="text-xs text-muted">
                      minted {relTime(t.created_at)} by{' '}
                      {t.created_by.name ?? t.created_by.email ?? t.created_by.user_id} ·{' '}
                      {t.last_used_at ? `last used ${relTime(t.last_used_at)}` : 'never used'} ·
                      expires {relTime(t.expires_at)}
                    </p>
                    <ul className="flex flex-wrap gap-1.5">
                      {t.allowed_tools.map((tool) => (
                        <li
                          key={tool}
                          className="inline-flex items-center px-2 py-0.5 rounded-md bg-surface-2 border border-line"
                        >
                          <Mono className="text-[11px]">{tool}</Mono>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => void revoke(t)}
                    disabled={busy}
                    className="!text-danger hover:!bg-danger-soft"
                  >
                    <IconTrash size={13} />
                    Revoke
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {revokedTokens.length > 0 && (
        <Card>
          <CardHeader
            title="Revoked deploy tokens"
            description="Retained for audit. These can't authenticate anymore."
          />
          <ul className="divide-y divide-line">
            {revokedTokens.map((t) => (
              <li key={t.id} className="px-5 py-3 text-sm">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1 space-y-0.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Mono className="font-medium text-muted">{t.label}</Mono>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-danger-soft text-danger font-medium uppercase tracking-wide">
                        {t.revoked_reason ?? 'revoked'}
                      </span>
                    </div>
                    <p className="text-xs text-muted">
                      minted by {t.created_by.name ?? t.created_by.email ?? t.created_by.user_id} ·
                      revoked {t.revoked_at ? relTime(t.revoked_at) : 'unknown'}
                    </p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
